import React, { useRef, useCallback } from 'react';
import { 
  Bold, Italic, Underline, Strikethrough, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Code, Link as LinkIcon,
  Heading1, Heading2, Heading3, Undo, Redo, Type
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const googleFonts = [
  { name: 'Padrão', value: 'Inter, sans-serif' },
  { name: 'Playfair Display', value: "'Playfair Display', serif" },
  { name: 'Montserrat', value: "'Montserrat', sans-serif" },
  { name: 'Roboto', value: "'Roboto', sans-serif" },
  { name: 'Open Sans', value: "'Open Sans', sans-serif" },
  { name: 'Lato', value: "'Lato', sans-serif" },
  { name: 'Poppins', value: "'Poppins', sans-serif" },
  { name: 'Merriweather', value: "'Merriweather', serif" },
  { name: 'Georgia', value: "Georgia, serif" },
];

const fontSizes = [
  { name: 'Pequeno', value: '12px' },
  { name: 'Normal', value: '16px' },
  { name: 'Médio', value: '18px' },
  { name: 'Grande', value: '24px' },
  { name: 'Extra Grande', value: '32px' },
];

const colors = [
  { name: 'Preto', value: '#1A1A1A' },
  { name: 'Verde', value: '#0F5A34' },
  { name: 'Vermelho', value: '#E53935' },
  { name: 'Dourado', value: '#DFAF3A' },
  { name: 'Cinza', value: '#5C5C5C' },
  { name: 'Branco', value: '#FFFFFF' },
];

export const RichTextEditor = ({ value, onChange, placeholder = 'Escreva o conteúdo aqui...' }) => {
  const editorRef = useRef(null);

  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    // Trigger onChange
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const insertLink = () => {
    const url = prompt('Digite a URL do link:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={`h-8 w-8 p-0 ${active ? 'bg-[#0F5A34]/10 text-[#0F5A34]' : 'text-[#5C5C5C] hover:text-[#0F5A34] hover:bg-[#0F5A34]/5'}`}
      title={title}
    >
      {children}
    </Button>
  );

  return (
    <div className="rich-text-editor border border-[#D6D2C4] rounded-md overflow-hidden">
      {/* Google Fonts Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&family=Inter:wght@300;400;500;600&family=Roboto:wght@300;400;500;700&family=Open+Sans:wght@300;400;500;600;700&family=Lato:wght@300;400;700&family=Poppins:wght@300;400;500;600;700&family=Merriweather:wght@300;400;700&display=swap');
      `}</style>
      
      {/* Toolbar */}
      <div className="bg-[#F9F8F6] border-b border-[#D6D2C4] p-2 flex flex-wrap items-center gap-1">
        {/* Font Family */}
        <Select onValueChange={(v) => execCommand('fontName', v)}>
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <Type size={14} className="mr-1" />
            <SelectValue placeholder="Fonte" />
          </SelectTrigger>
          <SelectContent>
            {googleFonts.map((font) => (
              <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                {font.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Font Size */}
        <Select onValueChange={(v) => execCommand('fontSize', v === '12px' ? '1' : v === '16px' ? '3' : v === '18px' ? '4' : v === '24px' ? '5' : '6')}>
          <SelectTrigger className="w-[100px] h-8 text-xs">
            <SelectValue placeholder="Tamanho" />
          </SelectTrigger>
          <SelectContent>
            {fontSizes.map((size) => (
              <SelectItem key={size.value} value={size.value}>
                {size.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Text Formatting */}
        <ToolbarButton onClick={() => execCommand('bold')} title="Negrito (Ctrl+B)">
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title="Itálico (Ctrl+I)">
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('underline')} title="Sublinhado (Ctrl+U)">
          <Underline size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('strikeThrough')} title="Tachado">
          <Strikethrough size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Text Color */}
        <Select onValueChange={(v) => execCommand('foreColor', v)}>
          <SelectTrigger className="w-[90px] h-8 text-xs">
            <SelectValue placeholder="Cor" />
          </SelectTrigger>
          <SelectContent>
            {colors.map((color) => (
              <SelectItem key={color.value} value={color.value}>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full border" style={{ backgroundColor: color.value }} />
                  {color.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Alignment */}
        <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Alinhar à Esquerda">
          <AlignLeft size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Centralizar">
          <AlignCenter size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyRight')} title="Alinhar à Direita">
          <AlignRight size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyFull')} title="Justificar">
          <AlignJustify size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Lists */}
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Lista com Marcadores">
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Lista Numerada">
          <ListOrdered size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Headers */}
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h1')} title="Título 1">
          <Heading1 size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h2')} title="Título 2">
          <Heading2 size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'h3')} title="Título 3">
          <Heading3 size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Quote and Code */}
        <ToolbarButton onClick={() => execCommand('formatBlock', 'blockquote')} title="Citação">
          <Quote size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'pre')} title="Código">
          <Code size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Link */}
        <ToolbarButton onClick={insertLink} title="Inserir Link">
          <LinkIcon size={16} />
        </ToolbarButton>

        <div className="w-px h-6 bg-[#D6D2C4] mx-1" />

        {/* Undo/Redo */}
        <ToolbarButton onClick={() => execCommand('undo')} title="Desfazer (Ctrl+Z)">
          <Undo size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('redo')} title="Refazer (Ctrl+Y)">
          <Redo size={16} />
        </ToolbarButton>
      </div>

      {/* Editor Area */}
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[300px] p-4 outline-none prose max-w-none"
        style={{ fontFamily: "'Inter', sans-serif" }}
        onInput={handleInput}
        dangerouslySetInnerHTML={{ __html: value || '' }}
        data-placeholder={placeholder}
      />

      <style>{`
        .rich-text-editor [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          pointer-events: none;
        }
        .rich-text-editor [contenteditable] h1 {
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }
        .rich-text-editor [contenteditable] h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }
        .rich-text-editor [contenteditable] h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .rich-text-editor [contenteditable] blockquote {
          border-left: 4px solid #0F5A34;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #5C5C5C;
        }
        .rich-text-editor [contenteditable] pre {
          background: #F3F4F6;
          padding: 1rem;
          border-radius: 4px;
          font-family: monospace;
          overflow-x: auto;
        }
        .rich-text-editor [contenteditable] ul, 
        .rich-text-editor [contenteditable] ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .rich-text-editor [contenteditable] a {
          color: #0F5A34;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
